import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Alert, Paper, Grid,
  Avatar, IconButton, Chip, CircularProgress
} from '@mui/material';
import { BookOpen, Plus, Pencil, Diamond, Archive, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import { createMiniZip, downloadBlob } from '../utils/miniZip';

export default function StoryflowPanel({ getToken }) {
  const navigate = useNavigate();
  const { activeTeamId } = useTeam();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedCount, setCachedCount] = useCachedPanelCount(activeTeamId ? `chapters:${activeTeamId}` : null, 6);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [formName, setFormName] = useState('');
  const [formNumber, setFormNumber] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [formPreview, setFormPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Decisions modal
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [decisionsLoading, setDecisionsLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [checkingSwitches, setCheckingSwitches] = useState(false);
  const [switchCheckResult, setSwitchCheckResult] = useState(null);

  const loadDecisions = useCallback(async () => {
    if (!activeTeamId) return;
    setDecisionsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/decisions?teamId=${encodeURIComponent(activeTeamId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDecisions(data.decisions || []);
      }
    } catch (_) {}
    setDecisionsLoading(false);
  }, [activeTeamId, getToken]);

  useEffect(() => {
    if (decisionsOpen) {
      loadDecisions();
      setResyncResult(null);
      setSwitchCheckResult(null);
    }
  }, [decisionsOpen, loadDecisions]);

  const checkSwitches = useCallback(async () => {
    if (!activeTeamId || checkingSwitches) return;
    setCheckingSwitches(true);
    setSwitchCheckResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/decisions/check-switches?teamId=${encodeURIComponent(activeTeamId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Check failed');
      const data = await res.json();
      setSwitchCheckResult(data);
    } catch (e) {
      setSwitchCheckResult({ error: e.message || 'Check failed' });
    } finally {
      setCheckingSwitches(false);
    }
  }, [activeTeamId, getToken, checkingSwitches]);

  const resyncDecisions = useCallback(async () => {
    if (!activeTeamId || resyncing) return;
    setResyncing(true);
    setResyncResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/decisions/resync?teamId=${encodeURIComponent(activeTeamId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Resync failed');
      const data = await res.json();
      setResyncResult(data);
      await loadDecisions();
    } catch (e) {
      setResyncResult({ error: e.message || 'Resync failed' });
    } finally {
      setResyncing(false);
    }
  }, [activeTeamId, getToken, loadDecisions, resyncing]);

  // ── Backups modal ──
  const [backupsOpen, setBackupsOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupPhase, setBackupPhase] = useState('idle'); // 'idle' | 'working' | 'ready'
  const [backupReady, setBackupReady] = useState(null); // backup record returned from POST
  const [backupError, setBackupError] = useState('');

  const loadBackups = useCallback(async () => {
    if (!activeTeamId) return;
    setBackupsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/storyflow-backups?teamId=${encodeURIComponent(activeTeamId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setBackups(data.backups || []);
      }
    } catch (_) { /* ignore */ }
    setBackupsLoading(false);
  }, [activeTeamId, getToken]);

  useEffect(() => {
    if (backupsOpen) {
      loadBackups();
      setBackupPhase('idle');
      setBackupReady(null);
      setBackupError('');
    }
  }, [backupsOpen, loadBackups]);

  const downloadBackupById = useCallback(async (backupId, createdAt) => {
    if (!activeTeamId) return;
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/storyflow-backups/${encodeURIComponent(backupId)}?teamId=${encodeURIComponent(activeTeamId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to load backup');
      const data = await res.json();
      const entries = Array.isArray(data.entries) ? data.entries : [];
      const decisions = Array.isArray(data.decisions) ? data.decisions : [];

      const zip = createMiniZip();
      // One file per storyflow with a safe, sortable filename.
      for (const entry of entries) {
        const num = entry.number == null ? '' : String(entry.number).padStart(2, '0');
        const safeName = String(entry.name || 'chapter').replace(/[\\/:*?"<>|]+/g, '_');
        const fileName = num ? `${num}-${safeName}.json` : `${safeName}.json`;
        const payload = {
          storyflowId: entry.storyflowId,
          name: entry.name,
          number: entry.number,
          graph: entry.graph || { nodes: [], edges: [] },
          graphMeta: entry.graphMeta || { positions: {}, viewport: null }
        };
        zip.addTextFile(`storyflows/${fileName}`, JSON.stringify(payload, null, 2));
      }
      // Decisions go as a single separate JSON file.
      zip.addTextFile('decisions.json', JSON.stringify(decisions, null, 2));
      // Manifest for humans rummaging through the archive.
      zip.addTextFile('manifest.json', JSON.stringify({
        id: data.id,
        teamId: activeTeamId,
        createdAt: data.createdAt,
        label: data.label || '',
        chapterCount: entries.length,
        decisionCount: decisions.length
      }, null, 2));

      const blob = zip.build();
      const stamp = new Date(createdAt || data.createdAt || Date.now())
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .slice(0, 19);
      downloadBlob(blob, `storyflow-backup-${stamp}.zip`);
    } catch (e) {
      setBackupError(e.message || 'Download failed');
    }
  }, [activeTeamId, getToken]);

  const createBackup = useCallback(async () => {
    if (!activeTeamId) return;
    setBackupPhase('working');
    setBackupError('');
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/storyflow-backups`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ teamId: activeTeamId })
        }
      );
      if (!res.ok) throw new Error('Backup failed');
      const data = await res.json();
      setBackupReady(data);
      setBackupPhase('ready');
      // Refresh the list so the new backup shows up.
      loadBackups();
    } catch (e) {
      setBackupError(e.message || 'Backup failed');
      setBackupPhase('idle');
    }
  }, [activeTeamId, getToken, loadBackups]);

  const deleteBackup = useCallback(async (backupId) => {
    if (!activeTeamId) return;
    try {
      const token = await getToken();
      await fetch(
        `${API_BASE_URL}/api/storyflow-backups/${encodeURIComponent(backupId)}?teamId=${encodeURIComponent(activeTeamId)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      setBackups((prev) => prev.filter((b) => b.id !== backupId));
    } catch (_) { /* ignore */ }
  }, [activeTeamId, getToken]);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const load = useCallback(async () => {
    if (!activeTeamId) return;
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storyflows?teamId=${encodeURIComponent(activeTeamId)}`, { headers: h });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      const list = data.storyflows || [];
      setFlows(list);
      setCachedCount(list.length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const hasBackstory = flows.some(f => f.number === 0);

  const openCreate = () => {
    setEditingFlow(null);
    setFormName('');
    const nextNumber = Math.max(0, ...flows.map(f => f.number ?? 0)) + 1;
    setFormNumber(String(nextNumber));
    setFormFile(null);
    setFormPreview(null);
    setModalOpen(true);
  };

  const openCreateBackstory = () => {
    setEditingFlow(null);
    setFormName('');
    setFormNumber('0');
    setFormFile(null);
    setFormPreview(null);
    setModalOpen(true);
  };

  const openEdit = (e, sf) => {
    e.stopPropagation();
    setEditingFlow(sf);
    setFormName(sf.name);
    setFormNumber(sf.number != null ? String(sf.number) : '');
    setFormFile(null);
    setFormPreview(sf.cover?.url128 || sf.cover?.url512 || null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingFlow(null);
    setSaving(false);
    setFormFile(null);
    setFormPreview(null);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFormFile(f);
    setFormPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('teamId', activeTeamId);
      fd.append('name', formName.trim());
      if (formNumber) fd.append('number', formNumber);
      if (formFile) fd.append('cover', formFile);

      const url = editingFlow
        ? `${API_BASE_URL}/api/storyflows/${editingFlow.id}`
        : `${API_BASE_URL}/api/storyflows`;
      const method = editingFlow ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      closeModal();

      if (!editingFlow) {
        // New chapter — navigate to graph editor
        navigate(`/storyflow/${encodeURIComponent(data.id)}`);
      } else {
        await load();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteFlow = async (e, sf) => {
    e.stopPropagation();
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storyflows/${sf.id}`, { method: 'DELETE', headers: h });
      if (!res.ok) throw new Error('Failed to delete');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ color: 'info.light', display: 'flex', alignItems: 'center' }}>
            <BookOpen size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Chapters
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          {!hasBackstory && (
            <Button variant="outlined" size="small" startIcon={<Plus size={16} />} onClick={openCreateBackstory}>
              Add backstory
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<Archive size={14} />} onClick={() => setBackupsOpen(true)}>
            Backups
          </Button>
          <Button variant="outlined" size="small" startIcon={<Diamond size={14} />} onClick={() => setDecisionsOpen(true)}>
            Decisions
          </Button>
          <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={openCreate}>
            Add chapter
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? <AdminPanelSkeleton variant="grid" count={cachedCount} /> : flows.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">No chapters yet.</Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {flows.map((sf) => (
            <Grid item xs={12} sm={6} md={4} key={sf.id}>
              <Paper
                variant="outlined"
                onClick={() => navigate(`/storyflow/${encodeURIComponent(sf.id)}`)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderColor: 'divider',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    variant="rounded"
                    src={sf.cover?.url128 || sf.cover?.url64 || undefined}
                    sx={{
                      width: 48, height: 48,
                      bgcolor: 'rgba(59, 130, 246, 0.14)',
                      color: 'info.light',
                      fontSize: '1rem', fontWeight: 700
                    }}
                  >
                    {sf.number ?? '?'}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    {sf.number != null && (
                      <Typography variant="caption" color="text.secondary">
                        {sf.number === 0 ? 'Backstory' : `Chapter ${sf.number}`}
                      </Typography>
                    )}
                    <Typography variant="subtitle2" fontWeight={600} noWrap>{sf.name}</Typography>
                  </Box>
                  <Box display="flex" gap={0.5}>
                    <IconButton size="small" onClick={(e) => openEdit(e, sf)} sx={{ color: 'text.secondary' }}>
                      <Pencil size={14} />
                    </IconButton>
                    <ConfirmDeleteButton onConfirm={(e) => deleteFlow(e, sf)} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <AdminModal
        open={modalOpen}
        onClose={closeModal}
        title={
          editingFlow
            ? (editingFlow.number === 0 ? 'Edit backstory' : 'Edit chapter')
            : (formNumber === '0' ? 'New backstory' : 'New chapter')
        }
        maxWidth="xs"
        actions={
          <>
            <Button onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!formName.trim() || saving}>
              {saving ? 'Saving...' : editingFlow ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} alignItems="center">
          <Avatar
            variant="rounded"
            src={formPreview || undefined}
            sx={{
              width: 96, height: 96, cursor: 'pointer',
              bgcolor: 'rgba(59, 130, 246, 0.14)',
              color: 'info.light', fontSize: '2rem'
            }}
            onClick={() => fileRef.current?.click()}
          >
            {formNumber || '?'}
          </Avatar>
          <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()}>
            {formPreview ? 'Change cover' : 'Upload cover'}
          </Button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
          <TextField label="Chapter number" size="small" fullWidth type="number" value={formNumber} onChange={(e) => setFormNumber(e.target.value)} />
          <TextField label="Name" size="small" fullWidth value={formName} onChange={(e) => setFormName(e.target.value)} autoFocus />
        </Box>
      </AdminModal>

      {/* Decisions modal */}
      <AdminModal
        open={decisionsOpen}
        onClose={() => setDecisionsOpen(false)}
        title="All Decisions"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={checkingSwitches ? <CircularProgress size={14} /> : <AlertTriangle size={14} />}
              onClick={checkSwitches}
              disabled={checkingSwitches || !activeTeamId}
            >
              {checkingSwitches ? 'Checking...' : 'Check switches'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={resyncing ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
              onClick={resyncDecisions}
              disabled={resyncing || !activeTeamId}
            >
              {resyncing ? 'Resyncing...' : 'Resync from graphs'}
            </Button>
          </Box>
        }
      >
        {resyncResult && (
          <Alert
            severity={resyncResult.error ? 'error' : 'success'}
            sx={{ mb: 1.5 }}
            onClose={() => setResyncResult(null)}
          >
            {resyncResult.error
              ? resyncResult.error
              : `Added ${resyncResult.added}, updated ${resyncResult.updated}, removed ${resyncResult.removed}, unchanged ${resyncResult.unchanged}.`}
          </Alert>
        )}
        {switchCheckResult && (
          switchCheckResult.error ? (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSwitchCheckResult(null)}>
              {switchCheckResult.error}
            </Alert>
          ) : switchCheckResult.issueCount === 0 ? (
            <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSwitchCheckResult(null)}>
              Checked {switchCheckResult.totalSwitches} switch{switchCheckResult.totalSwitches === 1 ? '' : 'es'} — no issues.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setSwitchCheckResult(null)}>
              <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                Found {switchCheckResult.issueCount} issue{switchCheckResult.issueCount === 1 ? '' : 's'} in {switchCheckResult.totalSwitches} switch{switchCheckResult.totalSwitches === 1 ? '' : 'es'}:
              </Box>
              {(() => {
                const byChapter = new Map();
                for (const iss of switchCheckResult.issues) {
                  const key = iss.chapterNumber ?? '-';
                  if (!byChapter.has(key)) byChapter.set(key, { chapterNumber: iss.chapterNumber, chapterName: iss.chapterName, items: [] });
                  byChapter.get(key).items.push(iss);
                }
                const groups = Array.from(byChapter.values()).sort((a, b) => (a.chapterNumber ?? 9999) - (b.chapterNumber ?? 9999));
                return groups.map((g) => (
                  <Box key={`${g.chapterNumber}-${g.chapterName}`} sx={{ mt: 0.75 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      Ch.{g.chapterNumber ?? '?'} — {g.chapterName || '(unnamed)'}
                    </Typography>
                    {g.items.map((iss, i) => (
                      <Box key={`${iss.nodeId}-${i}`} sx={{ pl: 1.5, mt: 0.25 }}>
                        {iss.type === 'unknown_decision' && (
                          <Typography sx={{ fontSize: '0.72rem' }}>
                            Switch → unknown decision <b>“{iss.decisionRef}”</b>
                            {iss.switchOptions.length > 0 && (
                              <span style={{ color: 'rgba(161,161,170,0.7)' }}> · opts: {iss.switchOptions.join(' / ')}</span>
                            )}
                          </Typography>
                        )}
                        {iss.type === 'stale_options' && (
                          <Typography sx={{ fontSize: '0.72rem' }}>
                            Switch <b>“{iss.decisionRef}”</b> · stale options: <b>{iss.staleOptions.join(' / ')}</b>
                            <span style={{ color: 'rgba(161,161,170,0.7)' }}> · expected: {iss.expectedOptions.join(' / ')}</span>
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ));
              })()}
            </Alert>
          )
        )}
        {decisionsLoading ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Loading...</Typography>
        ) : decisions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }}>No decisions yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(() => {
              const deduped = [];
              for (const d of decisions) {
                const key = `${d.chapterNumber}:${d.decisionName}`;
                const existing = deduped.find((x) => x.key === key);
                if (existing) { existing.count++; }
                else { deduped.push({ ...d, key, count: 1 }); }
              }
              return deduped.map((d) => {
                const opts = d.options || [];
                const isTrueFalse = opts.length === 2 && opts[0] === 'True' && opts[1] === 'False';
                return (
                  <Box key={d.key} sx={{ p: 1, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fafafa' }}>
                        <span style={{ color: 'rgba(161,161,170,0.7)', marginRight: 6 }}>Ch.{d.chapterNumber}</span>
                        {d.decisionName || '(unnamed)'}
                        {d.count > 1 && <span style={{ color: 'rgba(161,161,170,0.5)', marginLeft: 4 }}>({d.count})</span>}
                      </Typography>
                      {!isTrueFalse && opts.map((o, j) => (
                        <Typography key={j} sx={{ fontSize: '0.7rem', color: 'rgba(250,250,250,0.5)', pl: 1.5 }}>
                          • {o}
                        </Typography>
                      ))}
                    </Box>
                    <Chip
                      label={isTrueFalse ? 'True / False' : 'Multiple'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, bgcolor: 'rgba(244,114,182,0.1)', color: 'rgba(244,114,182,0.8)', border: '1px solid rgba(244,114,182,0.15)', mt: 0.25 }}
                    />
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </AdminModal>

      {/* Backups modal */}
      <AdminModal
        open={backupsOpen}
        onClose={() => setBackupsOpen(false)}
        title="Storyflow Backups"
        maxWidth="sm"
        actions={
          backupPhase === 'ready' ? (
            <>
              <Button onClick={() => { setBackupPhase('idle'); setBackupReady(null); }} disabled={!backupReady}>
                Done
              </Button>
              <Button
                variant="contained"
                startIcon={<Download size={14} />}
                onClick={() => backupReady && downloadBackupById(backupReady.id, backupReady.createdAt)}
              >
                Download zip
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<Archive size={14} />}
              onClick={createBackup}
              disabled={backupPhase === 'working' || !activeTeamId}
            >
              {backupPhase === 'working' ? 'Backing up...' : 'Make backup'}
            </Button>
          )
        }
      >
        {backupError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBackupError('')}>{backupError}</Alert>}

        {backupPhase === 'working' && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              Collecting graphs + decisions and saving snapshot...
            </Typography>
          </Box>
        )}

        {backupPhase === 'ready' && backupReady && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>
              Backup saved
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1 }}>
              {backupReady.chapterCount} chapter{backupReady.chapterCount === 1 ? '' : 's'} · {backupReady.decisionCount} decision{backupReady.decisionCount === 1 ? '' : 's'}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Click <b>Download zip</b> to save a local copy.
            </Typography>
          </Box>
        )}

        {backupPhase === 'idle' && (
          backupsLoading ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Loading...</Typography>
          ) : backups.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }}>
              No backups yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {backups.map((b) => {
                const d = b.createdAt ? new Date(b.createdAt) : null;
                const pad = (n) => String(n).padStart(2, '0');
                const stamp = d
                  ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
                  : '—';
                return (
                  <Box
                    key={b.id}
                    sx={{
                      p: 1.25,
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.06)',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.75
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: 'rgba(139,92,246,0.14)',
                        color: '#a78bfa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Archive size={17} strokeWidth={2} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fafafa' }}>
                        {stamp}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'rgba(250,250,250,0.5)' }}>
                        {b.chapterCount} chapter{b.chapterCount === 1 ? '' : 's'} · {b.decisionCount} decision{b.decisionCount === 1 ? '' : 's'}
                        {b.label ? ` · ${b.label}` : ''}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      title="Download"
                      onClick={() => downloadBackupById(b.id, b.createdAt)}
                      sx={{ color: 'primary.main' }}
                    >
                      <Download size={14} />
                    </IconButton>
                    <ConfirmDeleteButton onConfirm={() => deleteBackup(b.id)} />
                  </Box>
                );
              })}
            </Box>
          )
        )}
      </AdminModal>
    </Box>
  );
}
